import React, { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { FiMenu, FiLogOut } from 'react-icons/fi';
import Sidebar from './components/Sidebar';
import { WabaSwitcher } from './lib/waba';
import Dashboard from './pages/Dashboard';
import Templates from './pages/Templates';
import TestAPI from './pages/TestAPI';
import Settings from './pages/Settings';
import Logs from './pages/Logs';
import Media from './pages/Media';
import Scheduler from './pages/Scheduler';
import TestOCR from './pages/TestOCR';
import ScoredLeads from './pages/ScoredLeads';
import Leads from './pages/LeadsManagement';
import LeadsOverview from './pages/Leadsoverview';
import WhatsappCalling from './pages/WhatsappCalling';
import WhatsappChat from './pages/WhatsappChat';
import GoogleAds from './pages/GoogleAds';
import GoogleAdsSummary from './pages/GoogleAdsSummary';
import WhatsappAds from './pages/WhatsappAds';
import WhatsappAdsSummary from './pages/WhatsAppCampaignSummary';
import SupportAdmin from './pages/AdminSupportReport';
import Login from './pages/Login';

function AppRoutes({ isAuthenticated, authLoading, user, onLogout }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  // Close the drawer on navigation, and whenever we grow past the lg breakpoint
  // (otherwise a drawer opened on mobile stays "open" behind the static sidebar).
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e) => e.matches && setMenuOpen(false);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Prevent the page behind the drawer from scrolling on touch devices.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-700 text-sm">Checking authentication...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar open={menuOpen} onClose={() => setMenuOpen(false)} />

      {/* min-w-0 is load-bearing: without it a wide table or grid stretches this
          flex child and pushes the whole page into horizontal scroll. */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile top bar */}
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b bg-white px-4 py-3 lg:hidden">
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
            aria-expanded={menuOpen}
            className="-ml-2 flex h-11 w-11 items-center justify-center rounded-lg text-gray-700 hover:bg-gray-100"
          >
            <FiMenu className="text-xl" />
          </button>
          {/* Black, not the brand pink: at 16px bold this is body-weight text,
              and #F96168 on white is 3.03:1 — under the 4.5:1 a small label
              needs. The brand shows up in the logo and the accents instead. */}
          <span className="flex-1 truncate text-base font-bold text-ink">
            MAA PLATFORM
          </span>
          <WabaSwitcher />
          <button
            type="button"
            onClick={onLogout}
            aria-label="Logout"
            className="flex h-11 w-11 items-center justify-center rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            <FiLogOut className="text-lg" />
          </button>
        </header>

        {/* No padding here on purpose: most pages already provide their own
            outer padding, and stacking the two wasted ~64px of a 375px screen.
            Pages that have none use the .page helper from index.css. */}
        <main className="min-w-0 flex-1">
          {/* Desktop logout — the mobile one lives in the top bar.
              Naming the signed-in person is worth the line now that logins are
              individual: on a shared screen it is the only way to tell whose
              actions the audit log is about to record. */}
          <div className="hidden items-center justify-end gap-3 px-8 pt-6 lg:flex">
            {/* Which business this whole screen is about. Set here, read by
                every page — see lib/waba.js. */}
            <WabaSwitcher className="mr-auto" />
            {user?.name && (
              <span className="text-sm text-gray-600">
                {user.name}
                {user.role && user.role !== 'operator' && (
                  <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs uppercase tracking-wide text-gray-500">
                    {user.role}
                  </span>
                )}
              </span>
            )}
            <button
              type="button"
              onClick={onLogout}
              className="px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
            >
              Logout
            </button>
          </div>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/templates" element={<Templates />} />
          <Route path="/test-api" element={<TestAPI />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/logs" element={<Logs />} />
          <Route path="/media" element={<Media />} />
          <Route path="/scheduler" element={<Scheduler />} />
          <Route path="/ai-ocr" element={<TestOCR />} />
          <Route path="/lead-scoring" element={<ScoredLeads />} />
          <Route path="/leads-wizard" element={<Leads />} />
          <Route path="/leads-overview" element={<LeadsOverview />} />
          <Route path="/whatsapp-calling" element={<WhatsappCalling />} />
          <Route path="/whatsapp-chat" element={<WhatsappChat />} />
          <Route path="/google-ads" element={<GoogleAds />} />
          <Route path="/google-ads/summary" element={<GoogleAdsSummary />} />
          <Route path="/whatsapp-ads" element={<WhatsappAds />} />
          {/* The manager is now a view inside Whatsapp Ads. Kept as a redirect
              so existing bookmarks and links do not dead-end. */}
          <Route path="/campaign-manager" element={<Navigate to="/whatsapp-ads" replace />} />
          <Route path="/whatsapp-ads/summary" element={<WhatsappAdsSummary />} />
          <Route path="/merchantapp-admin" element={<SupportAdmin />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </main>
      </div>
    </div>
  );
}

export default function App() {
  const [authLoading, setAuthLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);

  // Every page in this app calls fetch directly, so this wrapper is the one
  // place that can speak for all of them.
  //
  //   credentials: 'include'  attaches the httpOnly access-token cookie the
  //                           server set at login. The token is never handed
  //                           to JavaScript, so a script injected into any
  //                           page cannot read it out and post it elsewhere.
  //   401 handling            access tokens expire (an hour by default). One
  //                           silent refresh, then retry; only if that fails
  //                           does the session actually end.
  useEffect(() => {
    const originalFetch = window.fetch.bind(window);
    let refreshing = null;

    const isOurApi = (input) => {
      const url = typeof input === 'string' ? input : input?.url || '';
      // Relative URLs are same-origin, which behind nginx is every backend.
      return url.startsWith('/api') || url.startsWith('/leads') || url.startsWith('/book-demo');
    };

    // The endpoints a refresh-and-retry must never wrap. /api/auth/me is
    // deliberately NOT here: a reload after the access token expired should
    // recover on the refresh token rather than bounce to the login screen.
    const NO_RETRY = ['/api/auth/refresh', '/api/auth/logout',
                      '/api/auth/otp/', '/api/auth/token'];
    const isAuthCall = (input) => {
      const url = typeof input === 'string' ? input : input?.url || '';
      return NO_RETRY.some((path) => url.startsWith(path));
    };

    window.fetch = async (input, init = {}) => {
      const next = { ...init };
      if (!next.credentials) next.credentials = 'include';

      const res = await originalFetch(input, next);
      if (res.status !== 401 || !isOurApi(input) || isAuthCall(input)) return res;

      // Collapse concurrent 401s onto one refresh — a dashboard page can have
      // five requests in flight, and five refreshes would rotate the refresh
      // token five times and invalidate four of them.
      if (!refreshing) {
        refreshing = originalFetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }).finally(() => { refreshing = null; });
      }

      const refreshed = await refreshing.catch(() => null);
      if (!refreshed?.ok) {
        setIsAuthenticated(false);
        setUser(null);
        return res;
      }
      return originalFetch(input, next);
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const checkAuth = async () => {
      try {
        const res = await fetch('/api/auth/me', {
          method: 'GET',
          headers: { Accept: 'application/json' },
          credentials: 'include',
        });
        if (!mounted) return;

        if (!res.ok) {
          setIsAuthenticated(false);
          return;
        }

        const data = await res.json();
        setIsAuthenticated(Boolean(data?.authenticated));
        setUser(data?.user || null);
      } catch {
        if (mounted) setIsAuthenticated(false);
      } finally {
        if (mounted) setAuthLoading(false);
      }
    };

    checkAuth();
    return () => {
      mounted = false;
    };
  }, []);

  const handleLoginSuccess = (signedInUser) => {
    setIsAuthenticated(true);
    setUser(signedInUser || null);
    setAuthLoading(false);
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        credentials: 'include',
      });
    } catch {
      // ignore network errors on logout
    } finally {
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  return (
    <Router>
      <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <Navigate to="/" replace />
            ) : (
              <Login onLoginSuccess={handleLoginSuccess} />
            )
          }
        />
        <Route
          path="/*"
          element={
            <AppRoutes
              isAuthenticated={isAuthenticated}
              authLoading={authLoading}
              user={user}
              onLogout={handleLogout}
            />
          }
        />
      </Routes>
    </Router>
  );
}
